import { useMemo } from 'react'
import { configureStore, getDefaultMiddleware, EnhancedStore } from '@reduxjs/toolkit'
import { combineReducers } from "redux";
import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import createWebStorage from 'redux-persist/lib/storage/createWebStorage'


// reducers
import { cartReducer, purchaseStepReducer } from './slices/carts/index'
import { itemListReducer, itemPaginateReducer, itemSearchOptionReducer } from './slices/items/index'
import customerReducer from './slices/customers/index'
import loginReducer from './slices/logins/index'
import guideListReducer from './slices/guide/index'
import gmoReducer from './slices/gmo/index'
import snackbarReducer from './slices/commons/snackbar'
import dialogReducer from './slices/commons/dialog'
import drawerReducer from './slices/commons/drawer'
import searchDrawerTabReducer from './slices/commons/searchDrawerTab'
import institutionSearchReducer from './slices/medical_institution/search'
import formsReducer from './slices/forms/index'
import { bookmarksReducer } from './slices/bookmarks/index'
import partnersPaginationReducer from './slices/partners/index'
import submitStatusReducer from './slices/commons/submitStatus'
import searchAgenciesReducer from './slices/medical_agencies/search'

// combineReducers
const reducer = combineReducers({
  cart: cartReducer,
  purchaseStep: purchaseStepReducer,
  customer: customerReducer,
  login: loginReducer,
  drawer: drawerReducer,
  itemList: itemListReducer,
  itemPaginate: itemPaginateReducer,
  itemSearchOption: itemSearchOptionReducer,
  guideList: guideListReducer,
  snackbar: snackbarReducer,
  dialog: dialogReducer,
  searchDrawerTab: searchDrawerTabReducer,
  gmo: gmoReducer,
  institutions: institutionSearchReducer,
  forms: formsReducer,
  bookmark: bookmarksReducer,
  partnersPagination: partnersPaginationReducer,
  submitStatus: submitStatusReducer,
  searchAgencies: searchAgenciesReducer,
});

// HACK: `redux-persist failed to create sync storage. falling back to noop storage.`の対応
// https://github.com/vercel/next.js/discussions/15687#discussioncomment-45319
const createNoopStorage = () => {
  return {
    getItem(_key) {
      return Promise.resolve(null)
    },
    setItem(_key, value) {
      return Promise.resolve(value)
    },
    removeItem(_key) {
      return Promise.resolve()
    },
  }
}

const rootReducer = (state, action) => {
  if (action.type === 'STORE_RESET') {
    return reducer(undefined, action)
  }
  return reducer(state, action)
}

const storage = typeof window !== 'undefined' ? createWebStorage('local') : createNoopStorage()

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  blacklist: ['gmo'] // localStorageに保存しないリスト
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

// storeを生成
// 作成したsliceを一つに纏めてstoreに突っ込む処理
const createStore = (preloadedState) => {
  return configureStore({
    reducer: persistedReducer,
    // サーバーから取得したデータやその他の既存のデータを使って状態をHYDRATEするために使用される
    preloadedState,
    middleware: getDefaultMiddleware({
      // redux-persist
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  });
};

// store ここで定義することで、サーバーサイドでも初期化できる様になる。
let store;
export const initializeStore = (preloadedState) => {
  // 既にstoreがあれば利用。なければcreate
  let _store = store ?? createStore(preloadedState);

  if (preloadedState && store) {
    // getState: 現在の状態
  _store = createStore({ ...store.getState(), ...preloadedState });
    store = undefined;
  }
  // SSG and SSR always create a new store コードがサーバー側かクライアント側かチェックしている
  if (typeof window === 'undefined') return _store;
  // Create the store once in the client
  if (!store) store = _store;
  return _store;
};


// メモ化
export function useStore(initialState) {
  const store = useMemo(() => initializeStore(initialState), [initialState])
  return store
}
